import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { MaterialIcon, Pill } from '@dsny/dsny-component-library'
import { Filteralt } from '@dsny/dsny-component-library/icons'
import {
  FiltersSection,
  Wrapper,
  FilterWrapper,
  FilterRowTitle,
  SortWrapper,
  Sort,
  ViewWrapper,
  ViewOptionsWrapper,
  GridOption,
  ListOption
} from './MenuOptions.styles'
import SettingActions from 'reducers/Setting'
import OptionActions from 'reducers/Options'
import SortDropdown from '../Dropdowns/SortDropdown/SortDropdown'
import FiltersMenu from '../Filtering/FiltersMenu/FiltersMenu'
import ActiveFiltersMenu from '../Filtering/ActiveFiltersMenu/ActiveFiltersMenu'
import MobileFiltersPanel from '../Filtering/MobileFiltersPanel/MobileFiltersPanel'
import { useDesktopMediaQuery } from 'utils/hooks/useMediaQueries'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'
import { createArchivedFilter, hasArchivedFilter } from 'utils/archiveFilter'
import ArchivedFilter from 'components/Filtering/ArchivedFilter/ArchivedFilter'
import { trackEvent } from 'utils/segmentService'
import { TRACKING_EVENTS, FILTER_ACTIONS, PLATFORMS } from 'constants/tracking'

const MenuOptions = ({ isSearching = false }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isDesktop = useDesktopMediaQuery()
  const isCompact = !isDesktop
  const location = useLocation()
  const { listView } = useSelector((state) => state.setting)
  const { activeFilters } = useSelector((state) => state.options)
  const { pathname } = location
  const isSearchPage = pathname.includes('/search')
  const shouldShowFilters = !isSearchPage && !isSearching
  const [showFilters, setShowFilters] = useState(false)
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()
  const archived = hasArchivedFilter(activeFilters)
  const handleArchivedChange = () => {
    const filter = createArchivedFilter(t('home.archived-releases'))
    dispatch(OptionActions.toggleFilter(filter))
    trackEvent(TRACKING_EVENTS.FILTER_INTERACTION, {
      action: archived ? FILTER_ACTIONS.REMOVE : FILTER_ACTIONS.SELECT,
      filter_type: filter.type,
      filter_label: filter.label,
      platform: PLATFORMS.DESKTOP,
      total_active_filters: String(activeFilters.length)
    })
  }

  const handleGridOption = (props) => {
    dispatch(SettingActions.toggleGridView())
  }

  const handleListOption = (props) => {
    dispatch(SettingActions.toggleListView())
  }

  if (isSearchPage || isSearching) return null

  const handleOpenMobileFilters = () => {
    setShowFilters(true)
  }

  const handleCloseMobileFilters = () => {
    setShowFilters(false)
  }

  return (
    <>
      {shouldShowFilters && (
        <>
          <FiltersSection>
            <Wrapper>
              <FilterWrapper>
                {isDesktop && (
                  <FilterRowTitle>{t('home.filter-by')}</FilterRowTitle>
                )}
                {!isCompact && <FiltersMenu />}
                {isDesktop && archiveToFilterEnabled && (
                  <ArchivedFilter
                    checked={archived}
                    label={t('collections.archived')}
                    onChange={handleArchivedChange}
                  />
                )}

                {isCompact && (
                  <Pill
                    onClick={handleOpenMobileFilters}
                    variant="tonal"
                    mode="dark"
                    label={
                      activeFilters.length > 0
                        ? `${t('home.filters-title')} (${activeFilters.length})`
                        : t('home.filters-title')
                    }
                    iconPosition="tailing"
                    icon={
                      <Filteralt
                        color={theme.colors.neutral90}
                        style={{ fontSize: 16 }}
                      />
                    }
                  />
                )}
              </FilterWrapper>

              <SortWrapper>
                {isDesktop && <Sort>{t('home.sort-by')}</Sort>}
                <div>
                  <SortDropdown />
                </div>
                <ViewWrapper>
                  <ViewOptionsWrapper>
                    <GridOption
                      $isGridActive={listView}
                      onClick={handleGridOption}
                    >
                      <MaterialIcon
                        name="GridView"
                        color={
                          listView
                            ? theme.colors.neutralW40
                            : theme.colors.neutralW100
                        }
                      />
                    </GridOption>
                    <ListOption
                      $isListActive={!listView}
                      onClick={handleListOption}
                    >
                      <MaterialIcon
                        name="ListView"
                        color={
                          !listView
                            ? theme.colors.neutralW40
                            : theme.colors.neutralW100
                        }
                      />
                    </ListOption>
                  </ViewOptionsWrapper>
                </ViewWrapper>
              </SortWrapper>
            </Wrapper>

            <ActiveFiltersMenu />
          </FiltersSection>

          <MobileFiltersPanel
            isOpen={isCompact && showFilters}
            onClose={handleCloseMobileFilters}
          />
        </>
      )}
    </>
  )
}

export default MenuOptions
